import React, { forwardRef, ReactNode, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomInput,
  DiscardModal,
  DropdownElement,
  InputReadOnly
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Store entities
import { updateCompanyBusinessAddress } from '../../../../../../store/company/company.thunks';

// Components
import { CountryOption } from '../../../../country-option/country-option';

// Styles
import './business-address.scss';

// Models
import { ICompanyProfile } from '../../../../../../interfaces/company.interfaces';
import { ICountry } from '../../../../../../interfaces/core.interfaces';

type TFormValues = {
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country_code: string;
};

export type TBusinessAddressRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TBusinessAddressProps = {
  company: ICompanyProfile;
  countries: ICountry[];
};

export const BusinessAddress = forwardRef<TBusinessAddressRef, TBusinessAddressProps>((
  { company, countries }: TBusinessAddressProps, ref
) => {
  const [form] = Form.useForm<TFormValues>();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const getCountryOptionId = (
    options: { name: string, code: string }[], name: string
  ): string => {
    return options
      .filter((opt) => name === opt?.name)
      .map((opt) => opt.code)?.[0] ?? company.country?.code
  };

  const generateOptions = (options: { name: string, code: string }[], isCountry: boolean = false): {
    label: ReactNode,
    value: string,
    disabled?: boolean,
  }[] => {
    return options.map((option) => (
      { label: isCountry ? <CountryOption country={ option as ICountry }/> : option?.name, value: option.name })
    )
  };

  const countriesOptions = [{
    label: 'Leave Empty',
    value: '---',
  }, ...generateOptions(countries, true)];

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    toggleEditMode();
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      form.resetFields();
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();
      return;
    }
    toggleDiscardModal();
  }

  const onValuesChange = (_: { [key: string]: string | string[] }, {
    country_code,
    zip_code,
    address,
    city,
    state
  }: TFormValues): void => {
    const isHasCountryChanges = company?.country?.name !== country_code;
    const isHasAddressChanges = company?.address !== address;
    const isHasCityChanges = company?.city !== city;
    const isHasStateChanges = company?.state !== state;
    const isHasZipCodeChanges = company?.zip_code !== zip_code;

    if (country_code === '---') {
      form.setFieldValue('country_code', null);
    }

    const isHasChanges = isHasCountryChanges || isHasAddressChanges || isHasCityChanges || isHasStateChanges || isHasZipCodeChanges;
    setHasChanges(isHasChanges);
  }

  const saveChanges = async (): Promise<void> => {
    const fieldsValue = form.getFieldsValue();
    setSavingChanges(true);
    const { payload } = await dispatch(updateCompanyBusinessAddress({
      ...fieldsValue,
      country_code: fieldsValue.country_code === null ? null : getCountryOptionId(countries, fieldsValue.country_code)
    }));
    setSavingChanges(false);
    if (!payload) return;
    setEditMode(false);
    setHasChanges(false);
    form.resetFields();
  }

  useImperativeHandle(ref, () => ({
    isHasChanges(): boolean {
      return hasChanges;
    },
    resetState(): void {
      form.setFieldsValue({
        address: company.address,
        city: company.city,
        state: company.state,
        zip_code: company.zip_code,
        country_code: company.country?.name,
      });
      setHasChanges(false);
      setEditMode(false);
      setSavingChanges(false);
    }
  }));

  return (
    <div className="business-address">
      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />
      <div className="business-address--label business--label">
        <div className="label-title">
          Business Address
        </div>
        <div className="label-actions">
          { !editMode &&
            <CustomButton type={ 'text' }
                          text={ (company?.city || company.country || company.state || company.address || company.zip_code) ? 'Edit' : 'Add' }
                          size={ 'small' }
                          onClick={ toggleEditMode }/>
          }

          { editMode && <>
            { hasChanges && <CustomButton type={ 'primary' }
                                          text={ 'Save' }
                                          size={ 'small' }
                                          loading={ savingChanges }
                                          onClick={ saveChanges }/> }
            <CustomButton type={ 'text' }
                          text={ 'Cancel' }
                          size={ 'small' }
                          disabled={ savingChanges }
                          onClick={ onCancelChangesHandler }/>
          </> }
        </div>
      </div>
      <Form className="business-address--body business--body-items" form={ form }
            onValuesChange={ onValuesChange }>
        <div className="body-item split-items">
          <div className="body-item--field">
            { (!editMode && company?.address) &&
              <InputReadOnly label={ 'Street Address' } value={ company.address } textEllipsis={ true }/>
            }

            { editMode &&
              <Form.Item name={ 'address' } initialValue={ company.address }>
                <CustomInput placeholder={ 'Street Address' } type={ 'text' }/>
              </Form.Item> }
          </div>
          <div className="body-item--field">
            { (!editMode && company?.city) &&
              <InputReadOnly label={ 'City' } value={ company.city } textEllipsis={ true }/>
            }

            { editMode &&
              <Form.Item name={ 'city' } initialValue={ company.city }>
                <CustomInput placeholder={ 'City' } type={ 'text' }/>
              </Form.Item> }
          </div>
        </div>

        <div className="body-item split-items">
          <div className="body-item--field">
            { (!editMode && company?.state) &&
              <InputReadOnly label={ 'State' } value={ company.state } textEllipsis={ true }/>
            }

            { editMode &&
              <Form.Item name={ 'state' } initialValue={ company.state }>
                <CustomInput placeholder={ 'State' } type={ 'text' }/>
              </Form.Item> }
          </div>
          <div className="body-item--field">
            { (!editMode && company?.zip_code) &&
              <InputReadOnly label={ 'Zip Code' } value={ company.zip_code } textEllipsis={ true }/>
            }

            { editMode &&
              <Form.Item name={ 'zip_code' } initialValue={ company.zip_code }>
                <CustomInput placeholder={ 'Zip Code' } type={ 'text' }/>
              </Form.Item> }
          </div>
          <div className="body-item--field">
            { (!editMode && company?.country) &&
              <InputReadOnly label={ 'Country' } value={ company.country?.name } textEllipsis={ true }/>
            }

            { editMode &&
              <Form.Item name={ 'country_code' } initialValue={ company.country?.name } valuePropName={ 'value' }>
                <DropdownElement options={ countriesOptions }
                                 withoutAll={ true }
                                 showSearch={ true }
                                 placeholder={ 'Country' }/>
              </Form.Item> }
          </div>
        </div>
      </Form>
    </div>
  );
})
